import { useCustomTranslation } from '../Hooks/useCustomTranslation'
import { PASSWORD_REGEXP } from './consts'

export const IP_ADDRESS_REGEX = /^(([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])\.){3}([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])$/

/**
 * validate password
 * @param password
 * @returns true = password is valid
 */
export const validPasswordChars = (password: string): boolean => {
  const charRegex = new RegExp(PASSWORD_REGEXP)
  return charRegex.test(password)
}

/**
 * @typedef {Object} PasswordValidationObject
 * @property {boolean} valid - whether or not the password is valid
 * @property {string} errorMessage? - user readable error message if password is not valid
 */

/**
 * Checks whether a password fulfillls all our requirements.
 * password rules can be found here: https://docs.e3dc.com/display/CLOUD/Password+Rules
 *
 * @param password the password in question
 * @param passwordRepeat second input of password for similarity validation
 * @param currentPassword current password in case a new password is set to override an old one
 * @param serverError error from outside this function i.e if the server returns a validation error
 * @returns {PasswordValidationObject}
 */
export const useValidPassword = (
  password: string,
  passwordRepeat?: string,
  currentPassword?: string,
  serverError?: string
): { valid: boolean; errorMessage?: string } => {
  const { t } = useCustomTranslation()

  /* password requirement if oldPassword given: oldPassword not empty */
  if (currentPassword !== undefined && currentPassword.length === 0) {
    return { valid: false, errorMessage: t('user.save.currentPasswordInputEmpty') }
  }

  /* password requirement: min length of 8 */
  if (password.length < 8) {
    return { valid: false, errorMessage: t('shell.login.passwordLengthRequire') }
  }

  /* password requirement: include specialChar or number */
  if (!validPasswordChars(password)) {
    return { valid: false, errorMessage: t('shell.login.passwordSpecialcharRequire') }
  }

  /* password requirement if passwordRepeat given: password equals passwordRepeat */
  if (passwordRepeat !== undefined && password !== passwordRepeat) {
    return { valid: false, errorMessage: t('user.save.passwordRepeatNotEqual') }
  }

  /* an error occured outside this function, e.g server returned a validation error like passwordBreached*/
  if (serverError) {
    return { valid: false, errorMessage: serverError }
  }

  return { valid: true }
}

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/

export const emailIsValid = (email: string): boolean => {
  return EMAIL_REGEX.test(email)
}
